"use client";

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useI18n } from '@/hooks/useI18n';
import { Globe } from 'lucide-react';

// Language display configuration
const LANGUAGE_CONFIG = {
  en: { flag: '🇺🇸', labelKey: 'settings.english' },
  es: { flag: '🇪🇸', labelKey: 'settings.spanish' },
  he: { flag: '🇮🇱', labelKey: 'settings.hebrew' },
} as const;

type LanguageSwitcherProps = {
  /** When "compact", only the dropdown is shown (no label or description). Useful for login or header. */
  variant?: 'default' | 'compact';
};

export function LanguageSwitcher({ variant = 'default' }: LanguageSwitcherProps) {
  const { t, currentLanguage, changeLanguage } = useI18n();
  const [isChanging, setIsChanging] = useState(false);

  const handleLanguageChange = async (newLanguage: string) => {
    setIsChanging(true);
    try {
      changeLanguage(newLanguage as 'en' | 'es' | 'he');
      // Show success message or toast here if needed
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsChanging(false);
    }
  };

  const currentConfig = LANGUAGE_CONFIG[currentLanguage as keyof typeof LANGUAGE_CONFIG];
  const isCompact = variant === 'compact';

  return (
    <div 
      className={isCompact ? 'rtl:text-right rtl:[direction:rtl]' : 'space-y-2 rtl:text-right rtl:[direction:rtl]'}
      data-snapland-id="language-switcher"
    >
      {!isCompact && (
        <Label className="flex items-center gap-2" data-snapland-id="language-switcher-label">
          <Globe className="h-4 w-4" data-snapland-id="language-switcher-icon" />
          {t('settings.language')}
        </Label>
      )}
      <Select value={currentLanguage} onValueChange={handleLanguageChange} disabled={isChanging}>
        <SelectTrigger 
          className={isCompact ? 'w-[140px] border-gray-300' : 'w-[180px] border-gray-300'}
          data-snapland-id="language-switcher-trigger"
        >
          <SelectValue placeholder={t('settings.selectLanguage')}>
            {currentLanguage && currentConfig && (
              <div className="flex items-center gap-2 rtl:flex-row-reverse" data-snapland-id="language-switcher-current-value">
                <span className="text-lg" data-snapland-id="language-switcher-current-flag">{currentConfig.flag}</span>
                {t(currentConfig.labelKey)}
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent data-snapland-id="language-switcher-content">
          <SelectItem value="en" data-snapland-id="language-switcher-option-en">
            <div className="flex items-center gap-2 rtl:flex-row-reverse">
              <span className="text-lg">🇺🇸</span>
              {t('settings.english')}
            </div>
          </SelectItem>
          <SelectItem value="es" data-snapland-id="language-switcher-option-es">
            <div className="flex items-center gap-2 rtl:flex-row-reverse">
              <span className="text-lg">🇪🇸</span>
              {t('settings.spanish')}
            </div>
          </SelectItem>
          <SelectItem value="he" data-snapland-id="language-switcher-option-he">
            <div className="flex items-center gap-2 rtl:flex-row-reverse">
              <span className="text-lg">🇮🇱</span>
              {t('settings.hebrew')}
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
      {!isCompact && (
        <p 
          className="text-sm text-muted-foreground rtl:text-right rtl:[direction:rtl]"
          data-snapland-id="language-switcher-description"
        >
          {t('settings.languageDescription')}
        </p>
      )}
    </div>
  );
}
